import { Button } from '@/components/ui/button';
import {
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  Command,
} from '@/components/ui/command';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown, ExternalLink } from 'lucide-react';
import { MultiSelectCombobox } from '../form/formComponent/FormMultiSelect';
import { UseFormReturn } from 'react-hook-form';
import { appEnv } from '@/config/env';
import { NavLink } from 'react-router-dom';

function CreateUserForm({
  form,
  onSubmit,
  roles,
  markets,
  loading,
  title,
}: {
  form: UseFormReturn<{
    email: string;
    role: string;
    marketRestrictions: string[];
  }>;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  roles: readonly { readonly label: string; readonly value: string }[];
  markets: {
    label: string;
    value: string;
  }[];
  loading: boolean;
  title: string;
}) {
  return (
    <div className='mb-16'>
      <Form {...form}>
        <h1 className='scroll-m-20 font-semibold tracking-tight lg:text-2xl mb-6'>
          {title}
        </h1>

        <form onSubmit={onSubmit} className='space-y-4'>
          <FormField
            control={form.control}
            name='email'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder='email' {...field} />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />
          <FormDescription>
            <p>
              Read more about{' '}
              <NavLink
                to={`${appEnv.docsUrl}/docs/faq#i-cant-toggle-markets-or-edit-planners-even-though-im-added-as-a-user`}
                target='__blank'
                className='inline-flex hover:text-gray-500 items-center gap-1 underline'
              >
                how the email works
                <ExternalLink className='inline' />
              </NavLink>
            </p>
          </FormDescription>

          <FormField
            control={form.control}
            name='role'
            render={({ field }) => (
              <FormItem className='flex flex-col'>
                <FormLabel>Role</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant='outline'
                        role='combobox'
                        className={cn(
                          'w-[200px] justify-between',
                          !field.value && 'text-muted-foreground',
                          '[&>span]:w-full [&>span]:justify-between',
                        )}
                      >
                        {field.value
                          ? roles.find((role) => role.value === field.value)
                              ?.label
                          : 'Select role'}
                        <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className='w-[200px] p-0'>
                    <Command>
                      <CommandInput placeholder='Search role...' />
                      <CommandList>
                        <CommandEmpty>No role found.</CommandEmpty>
                        <CommandGroup>
                          {roles.map((role) => (
                            <CommandItem
                              value={role.label}
                              key={role.value}
                              onSelect={() => {
                                form.setValue('role', role.value);
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  role.value === field.value
                                    ? 'opacity-100'
                                    : 'opacity-0',
                                )}
                              />
                              {role.label}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <FormDescription>
                  <ul className='list-[square] pl-4 pt-2'>
                    <li>
                      <b>Creator role</b>: Can edit all planner configurations +
                      translations / create new planners
                    </li>
                    <li>
                      <b>Editor role</b>: Can edit planner configurations, can{' '}
                      <b>not</b> edit translations, based on market restrictions
                    </li>
                  </ul>
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          {form.watch('role') === 'editor' && (
            <FormField
              control={form.control}
              name='marketRestrictions'
              render={({ field }) => (
                <>
                  <MultiSelectCombobox
                    items={markets}
                    selectedValues={field.value || []}
                    onSelectionChange={field.onChange}
                    placeholder='Select market restrictions'
                    description=''
                    disabled={false}
                    searchEnabled
                    searchPlaceholder='Search market'
                  />
                  <FormDescription>
                    <ul className='list-[square] pl-4 pt-2'>
                      <li>
                        <b>None</b>: User has no restrictions and can edit all
                        planner configurations across all markets
                      </li>
                      <li>
                        <b>Specific markets selected</b>: User can only edit
                        planner configurations for the selected markets
                      </li>
                    </ul>
                  </FormDescription>
                </>
              )}
            />
          )}
          <Button type='submit' isLoading={loading}>
            Submit
          </Button>
        </form>
      </Form>
    </div>
  );
}

export default CreateUserForm;
